import { assign, createMachine } from 'xstate';
import { areCredentialsValid } from '../credentials';
import { LDContext } from 'launchdarkly-js-client-sdk';
import { LaunchDarklyContexts } from '../../feature-flags/constants';

type Configuration = {
  authService: Bluebeam.Services.IAuthService;
  featureFlagService: Bluebeam.Services.IFeatureFlagService;
};

type Event =
  | {
      data?: never;
      type:
        | 'done.invoke.redirect'
        | 'error.platform.initialize'
        | 'error.platform.login'
        | 'error.platform.redirect';
    }
  | {
      data: {
        credentials?: Bluebeam.Auth.Models.Credentials;
        isRedirect: boolean;
      };
      type: 'done.invoke.initialize';
    }
  | {
      data: {
        credentials: Bluebeam.Auth.Models.Credentials;
      };
      type: 'done.invoke.identify';
    }
  | {
      data: {
        credentials: Bluebeam.Auth.Models.Credentials;
      };
      type: 'done.invoke.refreshTokens';
    }
  | {
      data: {
        credentials: Bluebeam.Auth.Models.Credentials;
      };
      type: 'done.invoke.login' | 'done.invoke.refreshingTokens';
    };

type State =
  | {
      context: { error?: never };
      value:
        | 'initializing'
        | 'redirecting'
        | 'redirected'
        | 'loggingIn'
        | 'identfying'
        | 'loggedIn'
        | { loggedInWithRefresh: 'idle' | 'refreshingTokens' };
    }
  | {
      context: { error: string };
      value: 'failed';
    };

const DEFAULT_ERROR_MESSAGE =
  'An unexpected error has occured. Please try again later';
const TOKEN_EXPIRATION_TOLERANCE = 300;

export const createLoginMachine = ({
  authService,
  featureFlagService,
}: Configuration) => {
  return createMachine<State['context'], Event, State>(
    {
      context: {},
      id: 'login',
      initial: 'initializing',
      states: {
        initializing: {
          invoke: {
            id: 'initialize',
            onDone: [
              {
                cond: 'hasValidCredentials',
                target: 'identifying',
              },
              {
                cond: 'hasAlreadyRedirected',
                target: 'loggingIn',
              },
              {
                target: 'redirecting',
              },
            ],
            onError: {
              actions: 'addError',
              target: 'failed',
            },
            src: 'initialize',
          },
        },
        redirecting: {
          invoke: {
            id: 'redirect',
            onDone: {
              target: 'redirected',
            },
            onError: {
              actions: 'addError',
              target: 'failed',
            },
            src: 'redirect',
          },
        },
        failed: {
          type: 'final',
        },
        redirected: {
          type: 'final',
        },
        loggingIn: {
          invoke: {
            id: 'login',
            onDone: {
              target: 'identifying',
            },
            onError: {
              actions: 'addError',
              target: 'failed',
            },
            src: 'login',
          },
        },
        identifying: {
          invoke: {
            id: 'identify',
            onDone: [
              {
                cond: 'hasRefreshToken',
                target: 'loggedInWithRefresh.idle',
              },
              {
                target: 'loggedIn',
              },
            ],
            onError: {
              actions: 'addError',
              target: 'failed',
            },
            src: 'identify',
          },
        },
        loggedIn: {},
        loggedInWithRefresh: {
          initial: 'idle',
          states: {
            idle: {
              after: {
                TOKEN_EXPIRATION_THRESHOLD_REACHED: {
                  target: 'refreshingTokens',
                },
              },
            },
            refreshingTokens: {
              invoke: {
                id: 'refreshTokens',
                onDone: {
                  cond: 'hasRefreshToken',
                  target: 'idle',
                },
                onError: {
                  actions: 'addError',
                  target: '#login.failed',
                },
                src: 'refreshTokens',
              },
            },
          },
        },
      },
    },
    {
      actions: {
        addError: assign<State['context'], Event>({
          error: DEFAULT_ERROR_MESSAGE,
        }),
      },
      delays: {
        TOKEN_EXPIRATION_THRESHOLD_REACHED: () => {
          const secondsUntilExpiration = (
            authService as Bluebeam.Services.IAuthRefreshableService
          ).getSecondsUntilAccessTokenExpiration();

          return (secondsUntilExpiration - TOKEN_EXPIRATION_TOLERANCE) * 1000;
        },
      },
      guards: {
        hasAlreadyRedirected: (_, event) => {
          if (event.type !== 'done.invoke.initialize') {
            return false;
          }

          return event.data.isRedirect;
        },
        hasRefreshToken: (_, event) => {
          if (
            event.type !== 'done.invoke.identify' &&
            event.type !== 'done.invoke.refreshTokens'
          ) {
            return false;
          }

          return Boolean(event.data.credentials.refreshToken);
        },
        hasValidCredentials: (_, event) => {
          if (event.type !== 'done.invoke.initialize') {
            return false;
          }

          try {
            areCredentialsValid(event.data.credentials ?? {});
            return true;
          } catch {
            return false;
          }
        },
      },
      services: {
        identify: async () => {
          const credentials = authService.getCredentials();

          const userContext: LDContext = {
            kind: LaunchDarklyContexts.User,
            key: credentials.userId,
            email: credentials.email.toLowerCase(),
            origin: window.location.origin,
          };

          await featureFlagService.setContext(userContext);

          return { credentials };
        },
        initialize: async () => {
          const hasExternalCredentials =
            await authService.hasExternalCredentials();

          if (hasExternalCredentials) {
            await authService.loginWithExternalCredentials();
          }

          const isRedirect = await authService.isRedirect();

          if (isRedirect) {
            return { isRedirect };
          }

          const isLoggedIn = authService.isLoggedIn();

          if (!isLoggedIn) {
            return { isRedirect };
          }

          const credentials = authService.getCredentials();

          return { isRedirect, credentials };
        },
        login: async () => {
          await authService.login();

          const isLoggedIn = authService.isLoggedIn();

          if (!isLoggedIn) {
            return {};
          }

          const credentials = authService.getCredentials();

          return { credentials };
        },
        redirect: authService.redirect,
        refreshTokens: async () => {
          await (
            authService as Bluebeam.Services.IAuthRefreshableService
          ).refreshTokens();

          const isLoggedIn = authService.isLoggedIn();

          if (!isLoggedIn) {
            return {};
          }

          const credentials = authService.getCredentials();

          return { credentials };
        },
      },
    }
  );
};
